import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export function sanitizeHtml(html: string, options?: any): string {
  const defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'mark', 's', 'del', 'ins',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'
    ],
    ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'width', 'height', 'class', 'style'],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus']
  };

  try {
    return String(DOMPurify.sanitize(html, { ...defaultConfig, ...options }));
  } catch {
    return '';
  }
}

/**
 * Sanitizes HTML content specifically for blog posts and articles
 * Allows more formatting options compared to the default sanitizer
 */
export function sanitizeBlogContent(html: string): string {
  return sanitizeHtml(html, {
    // Allow more HTML elements for rich content
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'mark', 's', 'del', 'ins',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'tfoot',
      'div', 'span', 'hr',
      'sup', 'sub'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'width', 'height',
      'class', 'style', 'target', 'rel',
      'colspan', 'rowspan',
      'align'  // For table alignment
    ],
    // Allow external links but ensure they open in new tabs for security
    ADD_ATTR: ['target'],
  });
}

/**
 * Sanitizes HTML content for user-generated content like comments
 * Uses more restrictive settings for security
 */
export function sanitizeUserContent(html: string): string {
  return sanitizeHtml(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u',
      'ul', 'ol', 'li',
      'blockquote', 'code',
      'a'
    ],
    ALLOWED_ATTR: [
      'href', 'title'
    ],
    // More restrictive for user content
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
}

/**
 * Validates and sanitizes URLs to prevent malicious redirects
 */
export function sanitizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    
    // Only allow http, https, and mailto protocols
    if (!['http:', 'https:', 'mailto:'].includes(parsedUrl.protocol)) {
      return '';
    }
    
    return parsedUrl.toString();
  } catch {
    // Invalid URL
    return '';
  }
}

/**
 * Escapes HTML entities in plain text to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Validates input to prevent code injection
 */
export function validateInput(input: string, maxLength: number = 1000): string {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Remove null bytes and control characters except newlines and tabs
  const cleaned = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Truncate to max length
  return cleaned.length > maxLength ? cleaned.substring(0, maxLength) : cleaned;
}

/**
 * Validates username format - allows only alphanumeric characters, underscores, and hyphens
 * Rejects emojis, special characters, and other non-standard characters
 */
export function validateUsername(username: string): { isValid: boolean; error?: string } {
  if (!username || typeof username !== 'string') {
    return { isValid: false, error: 'Uživatelské jméno je povinné' };
  }

  const trimmed = username.trim();

  if (!trimmed) {
    return { isValid: false, error: 'Uživatelské jméno je povinné' };
  }

  if (trimmed.length > 15) {
    return { isValid: false, error: 'Uživatelské jméno může mít maximálně 15 znaků' };
  }

  if (trimmed.length < 2) {
    return { isValid: false, error: 'Uživatelské jméno musí mít alespoň 2 znaky' };
  }

  // Allow alphanumeric characters (including Czech characters), underscores, and hyphens
  // This regex blocks emojis but allows unicode letters like š, č, ř, ž, etc.
  const validUsernameRegex = /^[\p{L}0-9_-]+$/u;
  
  if (!validUsernameRegex.test(trimmed)) {
    return { isValid: false, error: 'Uživatelské jméno může obsahovat pouze písmena, čísla, podtržítka a pomlčky' };
  }

  // Additional check for consecutive special characters
  if (trimmed.includes('--') || trimmed.includes('__') || trimmed.includes('_-') || trimmed.includes('-_')) {
    return { isValid: false, error: 'Uživatelské jméno nesmí obsahovat následující speciální znaky za sebou' };
  }

  // Must start and end with alphanumeric character (for usernames longer than 1 char)
  if (trimmed.length > 1 && (!trimmed.match(/^[\p{L}0-9]/u) || !trimmed.match(/[\p{L}0-9]$/u))) {
    return { isValid: false, error: 'Uživatelské jméno musí začínat a končit písmenem nebo číslem' };
  }

  return { isValid: true };
}
